import { useContext } from "react"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/v2/components/ui/card"
import VaasEditor from "~/v2/components/vaas/vaas-editor"
import VaasContext, { VaasProvider } from "~/v2/providers/vaas"
import type { VaasId } from "~/v2/types/vaas"

const VaasCourseContent = () => {
  const {
    actionError,
    createCombinedVersion,
    course,
    courseId,
    detailLoading,
    guardStatus,
    lesson,
    publishLoading,
    publishVaas,
    saveError,
    saveLoading,
    saveVaas,
    saveVaasCourseToKv,
    unpublishLoading,
    unpublishVaas,
    vaasCourseSaveLoading,
    versionLoading,
  } = useContext(VaasContext)

  if (detailLoading || guardStatus === "loading") {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading VAAS item...</p>
        </CardContent>
      </Card>
    )
  }

  if (guardStatus === "course_error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Course load failed</CardTitle>
          <CardDescription>
            Could not load VAAS course {courseId}.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (guardStatus === "lesson_error") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Lesson load failed</CardTitle>
          <CardDescription>
            Course loaded, but attached lesson could not be fetched.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (guardStatus === "no_lesson") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No lesson attached</CardTitle>
          <CardDescription>
            VAAS invariant broken. This course must have exactly one attached
            lesson.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (guardStatus === "multiple_lessons") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Too many lessons attached</CardTitle>
          <CardDescription>
            VAAS invariant broken. This course has more than one attached
            lesson.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (!course || !lesson) {
    return null
  }

  return (
    <VaasEditor
      actionError={actionError}
      createCombinedVersion={createCombinedVersion}
      course={course}
      lesson={lesson}
      publishLoading={publishLoading}
      publishVaas={publishVaas}
      saveError={saveError}
      saveLoading={saveLoading}
      saveVaas={saveVaas}
      saveVaasCourseToKv={saveVaasCourseToKv}
      unpublishLoading={unpublishLoading}
      unpublishVaas={unpublishVaas}
      vaasCourseSaveLoading={vaasCourseSaveLoading}
      versionLoading={versionLoading}
    />
  )
}

const VaasCourse = ({
  match,
}: {
  match: { params: { course_id: VaasId } }
}) => {
  const courseId = Number(match.params.course_id)

  return (
    <div className="overflow-auto">
      <div className="mx-auto flex h-full w-full max-w-7xl flex-col gap-6 px-6 py-8">
        <VaasProvider courseId={courseId}>
          <VaasCourseContent />
        </VaasProvider>
      </div>
    </div>
  )
}

export default VaasCourse
